import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Save, Trash2, ArrowUp, ArrowDown, Pencil, X, Video } from "lucide-react";
import { inputCls, btnPrimary, btnGhost, btnDanger, formatErr } from "@/components/admin/ui";
import { ytThumb } from "@/lib/youtube";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { title: "", url: "", caption: "" };

const VideoForm = ({ initial = EMPTY, onSubmit, onCancel, submitLabel, testidPrefix }) => {
    const [form, setForm] = useState(initial);
    const [saving, setSaving] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        const ok = await onSubmit({ ...form, caption: form.caption || null });
        setSaving(false);
        if (ok) setForm(initial);
    };

    return (
        <form onSubmit={submit} data-testid={`${testidPrefix}-form`} className="grid gap-3">
            <input
                required
                value={form.title}
                onChange={set("title")}
                placeholder="Video title"
                data-testid={`${testidPrefix}-title-input`}
                className={inputCls}
            />
            <input
                required
                value={form.url}
                onChange={set("url")}
                placeholder="YouTube link (watch, share or embed URL)"
                data-testid={`${testidPrefix}-url-input`}
                className={inputCls}
            />
            <input
                value={form.caption || ""}
                onChange={set("caption")}
                placeholder="Short caption (optional) — e.g. Official Music Video"
                data-testid={`${testidPrefix}-caption-input`}
                className={inputCls}
            />
            <div className="flex flex-wrap gap-3">
                <button type="submit" disabled={saving} data-testid={`${testidPrefix}-submit-button`} className={btnPrimary}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : submitLabel === "Add Video" ? <Plus size={13} /> : <Save size={13} />}
                    {saving ? "Saving…" : submitLabel}
                </button>
                {onCancel && (
                    <button type="button" onClick={onCancel} data-testid={`${testidPrefix}-cancel-button`} className={btnGhost}>
                        <X size={13} /> Cancel
                    </button>
                )}
            </div>
        </form>
    );
};

const VideoRow = ({ video, index, total, token, onChanged }) => {
    const [editing, setEditing] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const headers = { Authorization: `Bearer ${token}` };

    const save = async (body) => {
        try {
            await axios.put(`${API}/videos/${video.id}`, body, { headers });
            toast.success("Video updated.");
            setEditing(false);
            onChanged();
            return true;
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not save video."));
            return false;
        }
    };

    const remove = async () => {
        try {
            await axios.delete(`${API}/videos/${video.id}`, { headers });
            toast.success("Video removed.");
            onChanged();
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not delete video."));
        }
    };

    const move = async (direction) => {
        await onChanged(direction === "up" ? { move: [index, index - 1] } : { move: [index, index + 1] });
    };

    return (
        <div
            data-testid={`admin-video-row-${video.id}`}
            className="rounded-2xl border border-amber-500/20 bg-zinc-900/60 backdrop-blur-md p-4 sm:p-5 grid sm:grid-cols-[180px_1fr] gap-5"
        >
            <img
                src={ytThumb(video.youtube_id)}
                alt={video.title}
                className="w-full sm:w-[180px] aspect-video object-cover rounded-xl border border-amber-500/20"
            />
            <div>
                {editing ? (
                    <VideoForm
                        initial={{ title: video.title, url: video.url || video.youtube_id, caption: video.caption || "" }}
                        onSubmit={save}
                        onCancel={() => setEditing(false)}
                        submitLabel="Save Video"
                        testidPrefix={`admin-video-edit-${video.id}`}
                    />
                ) : (
                    <>
                        <h3 className="font-display text-base font-bold text-white">{video.title}</h3>
                        {video.caption && (
                            <p className="font-meta text-[10px] tracking-[0.25em] uppercase text-amber-400 mt-1.5">{video.caption}</p>
                        )}
                        <p className="font-meta text-[11px] text-zinc-500 mt-2 break-all">{video.youtube_id}</p>
                        <div className="flex flex-wrap gap-2 mt-4">
                            <button
                                onClick={() => move("up")}
                                disabled={index === 0}
                                data-testid={`admin-video-up-${video.id}`}
                                className={`${btnGhost} px-3`}
                                aria-label="Move up"
                            >
                                <ArrowUp size={13} />
                            </button>
                            <button
                                onClick={() => move("down")}
                                disabled={index === total - 1}
                                data-testid={`admin-video-down-${video.id}`}
                                className={`${btnGhost} px-3`}
                                aria-label="Move down"
                            >
                                <ArrowDown size={13} />
                            </button>
                            <button onClick={() => setEditing(true)} data-testid={`admin-video-edit-button-${video.id}`} className={btnGhost}>
                                <Pencil size={13} /> Edit
                            </button>
                            {confirming ? (
                                <>
                                    <button onClick={remove} data-testid={`admin-video-confirm-delete-${video.id}`} className={btnDanger}>
                                        <Trash2 size={13} /> Yes, delete
                                    </button>
                                    <button onClick={() => setConfirming(false)} data-testid={`admin-video-cancel-delete-${video.id}`} className={btnGhost}>
                                        Keep
                                    </button>
                                </>
                            ) : (
                                <button onClick={() => setConfirming(true)} data-testid={`admin-video-delete-${video.id}`} className={btnDanger}>
                                    <Trash2 size={13} /> Remove
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export const VideosManager = ({ token }) => {
    const [videos, setVideos] = useState(null);
    const headers = { Authorization: `Bearer ${token}` };

    const load = () => {
        axios
            .get(`${API}/videos`)
            .then((res) => setVideos(res.data))
            .catch(() => setVideos([]));
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChanged = async (action) => {
        if (action?.move) {
            const [from, to] = action.move;
            const next = [...videos];
            const [item] = next.splice(from, 1);
            next.splice(to, 0, item);
            setVideos(next);
            try {
                const { data } = await axios.put(`${API}/videos/reorder`, { ids: next.map((v) => v.id) }, { headers });
                setVideos(data);
            } catch {
                toast.error("Could not save the new order.");
                load();
            }
            return;
        }
        load();
    };

    const add = async (body) => {
        try {
            await axios.post(`${API}/videos`, body, { headers });
            toast.success("Video published to the site.");
            load();
            return true;
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail, "Could not add video."));
            return false;
        }
    };

    return (
        <section data-testid="admin-videos-tab">
            <div className="rounded-2xl border border-amber-500/25 bg-zinc-900/60 backdrop-blur-md p-5 sm:p-6">
                <p className="font-meta text-[10px] tracking-[0.3em] uppercase text-amber-400">Add a video</p>
                <h2 className="font-display text-lg font-bold text-white mt-2 mb-5">Publish to the Videos page</h2>
                <VideoForm onSubmit={add} submitLabel="Add Video" testidPrefix="admin-video-add" />
            </div>

            <div className="mt-6 space-y-5">
                {videos === null ? (
                    <div className="rounded-2xl border border-amber-500/10 bg-zinc-900/40 h-32 animate-pulse" />
                ) : videos.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-amber-500/25 bg-zinc-900/40 p-10 text-center" data-testid="admin-videos-empty">
                        <Video size={28} className="mx-auto text-amber-500/60" />
                        <p className="text-sm text-zinc-400 mt-3">
                            No videos yet. Paste a YouTube link above — it goes live on the Videos page instantly.
                        </p>
                    </div>
                ) : (
                    videos.map((v, i) => (
                        <VideoRow
                            key={v.id}
                            video={v}
                            index={i}
                            total={videos.length}
                            token={token}
                            onChanged={handleChanged}
                        />
                    ))
                )}
            </div>
        </section>
    );
};

export default VideosManager;
